import React, { useEffect, useState, useRef } from 'react';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import OSelect from '../../../../Components/OElements/OSelect';
import OInput from '../../../../Components/OElements/OInput';

// Helpers
import {
	getTransferCategoryList,
	getTransferList,
	getTransferDriverListQuery,
} from '../../../../helpers/transfer/backend_helper';
import { residue_config } from '../../../../config';

/** Valores fijos temporales para prueba de API (reemplazar por campos reales) */
const TRANSPORTE_DTE_PLACEHOLDERS = {
	PatenteCarro: 'PATN01',
	FchSalida: '2026-04-23',
	HraSalida: '09:00:00',
	FchLlegada: '2026-04-23',
};

export const TransporteSectionSpec = {
	getInitialValues: () => ({
		// Ejemplo Residuos
		transfer_category_ids: [],
		patente_id: '',
		matricula_id: '',
		chofer_id: '',

		// Campos Transporte
		transport_patente: '',
		transport_rut_trans: '',
		driver_rut: '',
		driver_name: '',
		transport_name: '',
	}),
	getValidationSchema: () => ({
		transfer_category_ids: Yup.array().of(Yup.string()).required('Requerido'),
		patente_id: Yup.string().when(
			'transfer_category_ids',
			(transfer_category_ids, schema) => {
				if (
					transfer_category_ids?.includes(
						residue_config.REACT_APP_TRANSFER_CATEGORY_GROUND,
					)
				) {
					return schema.required('Requerido');
				}
				return schema.notRequired();
			},
		),
		chofer_id: Yup.string().when('patente_id', (patente_id, schema) => {
			if (patente_id) {
				return schema.required('Requerido');
			}
			return schema.notRequired();
		}),
		// NUEVO: Matrícula requerida para transporte marítimo en residuos
		matricula_id: Yup.string().when(
			['guide_category', 'transfer_category_ids'],
			(guide_category, transfer_category_ids, schema) => {
				// Para residuos, la matrícula es requerida si se selecciona transporte marítimo
				if (
					guide_category === 'residuos' &&
					transfer_category_ids?.includes(
						residue_config.REACT_APP_TRANSFER_CATEGORY_MARITIME,
					)
				) {
					return schema.required(
						'Matrícula requerida para transporte marítimo',
					);
				}
				return schema.notRequired();
			},
		),

		// Campos Transporte
		transport_patente: Yup.string().required('Requerido'),
		transport_rut_trans: Yup.string().required('Requerido'),
		driver_rut: Yup.string().required('Requerido'),
		driver_name: Yup.string().required('Requerido'),
	}),
	toPayload: values => ({
		dtefacele: {
			Encabezado: {
				Transporte: {
					Patente: values.transport_patente || '',
					RUTTrans: values.transport_rut_trans || '',
					Chofer: {
						RUTChofer: values.driver_rut || '',
						NombreChofer: values.driver_name || '',
					},
					Aduana: {
						NombreTransp: values.transport_name || '',
					},
					PatenteCarro: TRANSPORTE_DTE_PLACEHOLDERS.PatenteCarro,
					FchSalida: TRANSPORTE_DTE_PLACEHOLDERS.FchSalida,
					HraSalida: TRANSPORTE_DTE_PLACEHOLDERS.HraSalida,
					FchLlegada: TRANSPORTE_DTE_PLACEHOLDERS.FchLlegada,
				},
			},
		},
	}),
};

export const TransporteSection = () => {
	const validation = useFormikContext();

	const [selectedPatente, setSelectedPatente] = useState(null);
	const [selectedChofer, setSelectedChofer] = useState(null);
	const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(true);
	const prevTransferCategoryRef = useRef(
		validation.values.transfer_category_ids,
	);

	// Limpiar campos dependientes cuando cambia el medio de transporte (no en mount)
	useEffect(() => {
		const current = validation.values.transfer_category_ids;
		const prev = prevTransferCategoryRef.current;
		prevTransferCategoryRef.current = current;

		// Evitar limpiar en mount o si no cambió
		const prevStr = Array.isArray(prev) ? prev.join(',') : '';
		const curStr = Array.isArray(current) ? current.join(',') : '';
		if (prevStr === curStr) return;

		validation.setFieldValue('patente_id', '', false);
		validation.setFieldValue('matricula_id', '', false);
	}, [validation.values.transfer_category_ids]);

	const isMaritimeSelected = validation.values.transfer_category_ids?.includes(
		residue_config.REACT_APP_TRANSFER_CATEGORY_MARITIME,
	);

	const isGroundSelected = validation.values.transfer_category_ids?.includes(
		residue_config.REACT_APP_TRANSFER_CATEGORY_GROUND,
	);

	const handlePatenteSelected = patente => {
		validation.setFieldValue(
			'patente_id',
			patente?._id || patente?.value || '',
		);
		// Setear directamente para evitar race condition con useEffect
		validation.setFieldValue('transport_patente', patente?.patent || '');
		validation.setFieldValue(
			'transport_rut_trans',
			patente?.transfer_company?.rut || '',
		);
		validation.setFieldValue(
			'transport_name',
			patente?.transfer_company?.name || '',
		);
		setSelectedPatente(patente);
	};

	const handleChoferSelected = chofer => {
		validation.setFieldValue('chofer_id', chofer?._id || chofer?.value || '');
		// Setear directamente para evitar race condition con useEffect
		validation.setFieldValue('driver_rut', chofer?.rut || '');
		validation.setFieldValue('driver_name', chofer?.name || '');
		setSelectedChofer(chofer);
	};

	useEffect(() => {
		if (selectedPatente) {
			validation.setFieldValue(
				'transport_patente',
				selectedPatente?.patent || '',
			);
			validation.setFieldValue(
				'transport_rut_trans',
				selectedPatente?.transfer_company?.rut || '',
			);
			validation.setFieldValue(
				'transport_name',
				selectedPatente?.transfer_company?.name || '',
			);
		}
	}, [selectedPatente]);

	useEffect(() => {
		if (selectedChofer) {
			validation.setFieldValue('driver_rut', selectedChofer?.rut || '');
			validation.setFieldValue('driver_name', selectedChofer?.name || '');
		}
	}, [selectedChofer]);

	return (
		<Card className='border mb-0'>
			<CardBody className='p-4'>
				<div className='pb-2'>
					<h5 className=''>Información de transporte</h5>
				</div>
				<Row className='g-3'>
					<Col lg={4}>
						<OSelect
							isMulti
							label='Medio de transporte'
							id='transfer_category_ids'
							name='transfer_category_ids'
							value={validation.values.transfer_category_ids || []}
							requestApi={() => getTransferCategoryList()}
							handleLabelOptions={item => item.name}
							disabled={false}
							validation={validation}
						/>
					</Col>
					{isGroundSelected && (
						<>
							<Col lg={4}>
								<OSelect
									key={`patente_${
										validation.values.transfer_category_ids || ''
									}`}
									label='Patente'
									id='patente_id'
									name='patente_id'
									value={validation.values.patente_id || ''}
									handleLabelOptions={item =>
										`${item.patent} | ${
											item.transfer_company?.name || ''
										}`
									}
									onSelect={handlePatenteSelected}
									requestApi={() =>
										getTransferList({
											transfer_category_id:
												residue_config.REACT_APP_TRANSFER_CATEGORY_GROUND,
										})
									}
									disabled={false}
									validation={validation}
								/>
							</Col>
							<Col lg={4}>
								<OSelect
									key={`chofer_${
										validation.values.transfer_category_ids || ''
									}_${validation.values.patente_id || ''}`}
									label='Chofer'
									id='chofer_id'
									name='chofer_id'
									value={validation.values.chofer_id || ''}
									handleLabelOptions={item =>
										`${item.name} | ${item.rut}`
									}
									onSelect={handleChoferSelected}
									requestApi={async () => {
										const response = await getTransferDriverListQuery(
											{
												filters: {
													...(validation.values.patente_id &&
													selectedPatente?.transfer_company?._id
														? {
																transfer_company_id: {
																	$eq:
																		selectedPatente
																			?.transfer_company
																			?._id || '',
																},
															}
														: {}),
												},
												pagination: {
													page: 1,
													pageSize: 10,
												},
											},
										);

										return response.data;
									}}
									disabled={false}
									validation={validation}
								/>
							</Col>
						</>
					)}
					{isMaritimeSelected && (
						<Col lg={4}>
							<OSelect
								key={`matricula_${
									validation.values.transfer_category_ids || ''
								}`}
								label='Matrícula'
								id='matricula_id'
								name='matricula_id'
								value={validation.values.matricula_id || ''}
								handleLabelOptions={item =>
									`${item.patent} | ${
										item.transfer_company?.name || ''
									}`
								}
								requestApi={() =>
									getTransferList({
										transfer_category_id:
											residue_config.REACT_APP_TRANSFER_CATEGORY_MARITIME,
									})
								}
								// disabled={!isMaritimeSelected}
								validation={validation}
							/>
						</Col>
					)}
					{/* Divider with centered toggle */}
					<Col lg={12} className='p-0'>
						<div className='divider-with-toggle'>
							<hr className='hr' />
							<button
								type='button'
								className='divider-toggle-btn'
								aria-expanded={!isDetailsCollapsed}
								onClick={() => setIsDetailsCollapsed(prev => !prev)}
							>
								<i className='ri-arrow-up-s-line'></i>
							</button>
						</div>
					</Col>
					{/* Campos enviados al API */}
					<Col lg={12} className='p-0'>
						<div
							className={
								'collapsible-content' +
								(isDetailsCollapsed ? ' is-collapsed' : '')
							}
						>
							<Row className='g-3'>
								<Col lg={6}>
									<OInput
										id='transport_patente'
										name='transport_patente'
										label='Patente'
										type='text'
										className='form-control'
										value={validation.values.transport_patente || ''}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={6}>
									<OInput
										id='transport_rut_trans'
										name='transport_rut_trans'
										label='RUT del transportista'
										type='text'
										className='form-control'
										value={
											validation.values.transport_rut_trans || ''
										}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={4}>
									<OInput
										id='driver_rut'
										name='driver_rut'
										label='RUT del chofer'
										type='text'
										className='form-control'
										value={validation.values.driver_rut || ''}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
								<Col lg={8}>
									<OInput
										id='driver_name'
										name='driver_name'
										type='text'
										label='Nombre del chofer'
										className='form-control'
										value={validation.values.driver_name || ''}
										onChange={validation.handleChange}
										validation={validation}
										disabled
										readOnly
									/>
								</Col>
							</Row>
						</div>
					</Col>
				</Row>
			</CardBody>
		</Card>
	);
};
